import React, { useState } from 'react';
import ExportDropdown from './ExportDropdown';

/**
 * Income Allocation Report Component
 *
 * Displays allocated income within specific date ranges
 * Shows breakdown by clinician with client payments, insurance payments, and total allocated
 *
 * Features:
 * - Date range picker
 * - Payment source filter dropdown
 * - Include Insurance toggle
 * - Two tabs: Current payments and Insurance payer allocation
 * - Table with clinician data
 * - Pagination controls
 * - Export functionality
 *
 * TODO: Connect to real patient/appointment data from services
 */

interface IncomeAllocationData {
  clinician: string;
  totalClientPayments: number;
  totalInsurancePayments: number;
  totalAllocated: number;
}

interface IncomeAllocationReportProps {
  onBack?: () => void;
}

const IncomeAllocationReport: React.FC<IncomeAllocationReportProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'current' | 'insurance'>('current');
  const [startDate, setStartDate] = useState('09/16/2025');
  const [endDate, setEndDate] = useState('10/15/2025');
  const [paymentSource, setPaymentSource] = useState('all');
  const [includeInsurance, setIncludeInsurance] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Mock data - TODO: Replace with real data from services
  const mockData: IncomeAllocationData[] = [];

  const totalClientPayments = mockData.reduce((sum, item) => sum + item.totalClientPayments, 0);
  const totalInsurancePayments = mockData.reduce((sum, item) => sum + item.totalInsurancePayments, 0);
  const totalAllocated = mockData.reduce((sum, item) => sum + item.totalAllocated, 0);

  return (
    <div style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      padding: '1rem'
    }}>
      {/* Sample Data Notice */}
      <div style={{
        backgroundColor: '#FEF3C7',
        border: '1px solid #F59E0B',
        borderRadius: '6px',
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.875rem',
        color: '#92400E'
      }}>
        <span style={{ fontSize: '1.1rem' }}>&#9888;</span>
        <span><strong>Sample Data</strong> — This report will populate with real billing data as appointments and payments are recorded.</span>
      </div>
      {/* Info Banner */}
      <div style={{
        backgroundColor: '#e8f4f8',
        border: '1px solid #bee3f8',
        borderRadius: '6px',
        padding: '1rem 1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{
            fontSize: '1.25rem',
            color: '#2563eb'
          }}>ⓘ</span>
          <span style={{
            fontSize: '0.875rem',
            color: '#1e40af'
          }}>
            Your report is being generated, we'll email you once it's ready. The report will be available in Data Exports in your account settings for the next week.
          </span>
        </div>
        <button style={{
          backgroundColor: '#2563eb',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          fontWeight: '600',
          cursor: 'pointer'
        }}>
          View Data Exports
        </button>
      </div>

      {/* Breadcrumb */}
      <div style={{
        fontSize: '0.875rem',
        color: '#d1d5db',
        marginBottom: '1rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#d1d5db',
            textDecoration: 'none',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit'
          }}
        >
          Analytics
        </button>
        {' / '}
        <span style={{ color: '#111827' }}>Income allocation</span>
      </div>

      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.875rem',
            fontWeight: '700',
            color: '#111827',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            Income allocation
          </h1>
          <p style={{
            fontSize: '0.875rem',
            color: '#d1d5db',
            margin: 0
          }}>
            View your allocated income within specific date ranges.{' '}
            <span style={{ color: '#2563eb' }}>Learn more</span>.
          </p>
        </div>
        <ExportDropdown />
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '2rem',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '1.5rem'
      }}>
        <button
          onClick={() => setActiveTab('current')}
          style={{
            background: 'none',
            border: 'none',
            padding: '0.75rem 0',
            fontSize: '0.875rem',
            fontWeight: activeTab === 'current' ? '600' : '400',
            color: activeTab === 'current' ? '#2563eb' : '#6b7280',
            cursor: 'pointer',
            borderBottom: activeTab === 'current' ? '3px solid #2563eb' : '3px solid transparent',
            marginBottom: '-2px'
          }}
        >
          Current payments
        </button>
        <button
          onClick={() => setActiveTab('insurance')}
          style={{
            background: 'none',
            border: 'none',
            padding: '0.75rem 0',
            fontSize: '0.875rem',
            fontWeight: activeTab === 'insurance' ? '600' : '400',
            color: activeTab === 'insurance' ? '#2563eb' : '#6b7280',
            cursor: 'pointer',
            borderBottom: activeTab === 'insurance' ? '3px solid #2563eb' : '3px solid transparent',
            marginBottom: '-2px'
          }}
        >
          Insurance payer allocation
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Date Range Picker */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#ffffff'
        }}>
          <span style={{ fontSize: '1rem', color: '#2563eb' }}>📅</span>
          <input
            type="text"
            value={`${startDate} - ${endDate}`}
            readOnly
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '0.875rem',
              color: '#111827',
              width: '180px',
              cursor: 'pointer'
            }}
          />
        </div>

        {/* Payment Source Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem', color: '#d1d5db' }}>☰</span>
          <select
            value={paymentSource}
            onChange={(e) => setPaymentSource(e.target.value)}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              color: '#111827',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Payment Sources</option>
            <option value="client">Client Payments Only</option>
            <option value="insurance">Insurance Payments Only</option>
          </select>
        </div>

        {/* Include Insurance Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={includeInsurance}
              onChange={(e) => setIncludeInsurance(e.target.checked)}
              style={{
                width: '40px',
                height: '20px',
                cursor: 'pointer'
              }}
            />
            <span style={{
              fontSize: '0.875rem',
              color: '#374151'
            }}>
              Include Insurance
            </span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '1rem'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Clinician
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                  Total client payments
                  <span style={{ fontSize: '0.875rem', color: '#d1d5db', cursor: 'help' }}>ⓘ</span>
                </div>
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                  Total insurance payments
                  <span style={{ fontSize: '0.875rem', color: '#d1d5db', cursor: 'help' }}>ⓘ</span>
                </div>
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                  Total allocated
                  <span style={{ fontSize: '0.875rem', color: '#d1d5db', cursor: 'help' }}>ⓘ</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Totals Row */}
            <tr style={{
              backgroundColor: '#f3f4f6',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <td style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#111827'
              }}>
                Totals
              </td>
              <td style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#111827'
              }}>
                --
              </td>
              <td style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#111827'
              }}>
                --
              </td>
              <td style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#111827'
              }}>
                --
              </td>
            </tr>

            {/* Empty State */}
            {mockData.length === 0 && (
              <tr>
                <td colSpan={4} style={{
                  padding: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  No data available for the selected date range
                </td>
              </tr>
            )}

            {/* Data Rows */}
            {mockData.map((item, index) => (
              <tr key={index} style={{
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  {item.clinician}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  ${item.totalClientPayments.toFixed(2)}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  ${item.totalInsurancePayments.toFixed(2)}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  ${item.totalAllocated.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.875rem',
        color: '#d1d5db'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>Rows per page</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.25rem 0.5rem',
              fontSize: '0.875rem',
              backgroundColor: '#ffffff'
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span>1-0</span>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              disabled
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: 'not-allowed',
                color: '#d1d5db'
              }}
            >
              ⏮
            </button>
            <button
              disabled
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: 'not-allowed',
                color: '#d1d5db'
              }}
            >
              ◀
            </button>
            <button
              disabled
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: 'not-allowed',
                color: '#d1d5db'
              }}
            >
              ▶
            </button>
            <button
              disabled
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: 'not-allowed',
                color: '#d1d5db'
              }}
            >
              ⏭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeAllocationReport;
